import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption, type SSGroup } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
/** TR sayı biçimi: 12.500 → 12500 · 1.234,56 → 1234.56 */
function parseTrNumber(raw: string): number {
  const s = raw.trim();
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}

interface ShiftLite { id: string; name: string; startTime: string; endTime: string; isActive: boolean; sortOrder: number }

/** İçinde bulunulan vardiya (Europe/Istanbul duvar saati, gece-aşımı). */
function currentShiftIdFrom(shifts: ShiftLite[]): string | null {
  if (shifts.length === 0) return null;
  const parts = new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
  const hh = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const mm = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const t = (hh % 24) * 60 + mm;
  const toMin = (s: string) => { const [h, m] = s.split(":"); return Number(h) * 60 + Number(m); };
  for (const s of [...shifts].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const start = toMin(s.startTime), end = toMin(s.endTime);
    const inRange = start === end ? true : start < end ? t >= start && t < end : t >= start || t < end;
    if (inRange) return s.id;
  }
  return null;
}

const REASONS: Array<{ code: string; label: string }> = [
  { code: "fire", label: "Fire / kayıp" },
  { code: "numune", label: "Numune / analiz" },
  { code: "temizlik", label: "Temizlik / hat yıkama" },
  { code: "deneme", label: "Deneme / Ar-Ge" },
  { code: "diger", label: "Diğer" },
];

/** İstanbul yerel bugünü (YYYY-MM-DD). */
function istToday(): string {
  return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

/**
 * Manuel tüketim kaydı — reçete/siparişten bağımsız. Tür+ürün seç, tarih+vardiya, FEFO otomatik
 * ya da elle lot; stok yetersizse eksiye düşülebilir (allowNegative). inventory.adjustment akışının
 * tüketime uyarlanmış hali; .cons-fgrid ile yoğun (dikeyde minimal) yerleşim.
 */
export function ConsumptionEntryDrawer({ open, onClose, onDone }: {
  open: boolean;
  onClose: () => void;
  onDone: (msg: string, warnings: string[]) => void;
}) {
  const qc = useQueryClient();
  const [itemSel, setItemSel] = useState(""); // "kind:id"
  const [date, setDate] = useState(istToday());
  const [shiftId, setShiftId] = useState(""); // "" → otomatik (tarihten)
  const [shiftTouched, setShiftTouched] = useState(false);
  const [lotMode, setLotMode] = useState<"fefo" | "manual">("fefo");
  const [rowKey, setRowKey] = useState(""); // `${lotId}:${locationId}`
  const [qtyStr, setQtyStr] = useState("");
  const [reason, setReason] = useState(REASONS[0]!.code);
  const [notes, setNotes] = useState("");
  const [allowNegative, setAllowNegative] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const wsQ = trpc.settings.workSchedule.get.useQuery(undefined, { enabled: open, staleTime: 5 * 60_000 });
  const createMut = trpc.consumption.create.useMutation();

  const [kind, itemId] = useMemo(() => {
    if (!itemSel) return [undefined, ""] as const;
    const [k, i] = itemSel.split(":");
    return [k as "stock" | "semi" | "finished", i ?? ""] as const;
  }, [itemSel]);

  const balQ = trpc.inventory.balanceByItem.useQuery(
    { itemKind: kind ?? "stock", itemId },
    { enabled: open && lotMode === "manual" && !!kind && !!itemId },
  );

  const shifts = useMemo<ShiftLite[]>(() => (wsQ.data?.shifts ?? []).filter((s) => s.isActive), [wsQ.data]);

  // Açılışta / vardiya listesi gelince: kullanıcı elle değiştirmediyse içinde bulunulan vardiyayı seç.
  useEffect(() => {
    if (!open) return;
    if (shiftTouched) return;
    if (date === istToday()) setShiftId(currentShiftIdFrom(shifts) ?? "");
    else setShiftId(""); // geçmiş gün → otomatik (backend tarihten çözer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shifts, date, shiftTouched]);

  // Drawer açılınca formu sıfırla.
  useEffect(() => {
    if (open) {
      setItemSel(""); setDate(istToday()); setShiftId(""); setShiftTouched(false);
      setLotMode("fefo"); setRowKey(""); setQtyStr(""); setReason(REASONS[0]!.code);
      setNotes(""); setAllowNegative(false); setErr(null); createMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const itemGroups = useMemo<SSGroup[]>(() => {
    const all = (itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;
    const mk = (k: string, label: string): SSGroup | null => {
      const items = all.filter((r) => r.kind === k).map<SSOption>((r) => ({ value: `${r.kind}:${r.id}`, label: r.name, tail: r.code }));
      return items.length ? { group: label, items } : null;
    };
    return [mk("stock", "Hammadde / Sarf"), mk("semi", "Yarı Mamul"), mk("finished", "Mamul")].filter((g): g is SSGroup => g != null);
  }, [itemOptsQ.data]);

  const balRows = balQ.data?.rows ?? [];
  // Yalnız serbest (released) + SKT geçmemiş lotlar tüketilebilir (CLAUDE.md kural 9; FEFO ile aynı kapı).
  // Karantina/QC-bekleyen/red/SKT-geçmiş lotlar picker'da gösterilmez (backend de reddeder).
  const consumableRows = useMemo(
    () => balRows.filter((b) => b.lotStatus === "released" && (!b.expiresAt || new Date(b.expiresAt).getTime() > Date.now())),
    [balRows],
  );
  const lotOptions = useMemo<SSOption[]>(() => consumableRows.map((b) => ({
    value: `${b.lotId}:${b.locationId}`,
    label: `${b.lotCode} · ${b.locationName}`,
    desc: b.expiresAt ? `SKT ${new Intl.DateTimeFormat("tr-TR").format(new Date(b.expiresAt))}` : undefined,
    tail: `${fmtQty(b.qty)} ${b.uom}`,
  })), [consumableRows]);
  const selectedLot = useMemo(() => consumableRows.find((b) => `${b.lotId}:${b.locationId}` === rowKey) ?? null, [consumableRows, rowKey]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!kind || !itemId) return setErr("Bir ürün seçin.");
    const n = qtyStr.trim() ? parseTrNumber(qtyStr) : NaN;
    if (!Number.isFinite(n) || n <= 0) return setErr("Geçerli bir miktar girin (TR biçimi: 12,5 veya 1.234,56).");
    if (lotMode === "manual" && !selectedLot) return setErr("Elle lot seçiminde lot + lokasyon seçin.");
    if (lotMode === "manual" && selectedLot && n > selectedLot.qty && !allowNegative) {
      return setErr(`Seçili lot bakiyesi (${fmtQty(selectedLot.qty)} ${selectedLot.uom}) yetersiz — eksiye düşmek için onay kutusunu işaretleyin.`);
    }
    try {
      const res = await createMut.mutateAsync({
        itemKind: kind,
        itemId,
        qty: n,
        date,
        shiftId: shiftId || undefined,
        reasonCode: reason,
        notes: notes.trim() || undefined,
        allowNegative,
        ...(lotMode === "manual" && selectedLot ? { lotId: selectedLot.lotId, locationId: selectedLot.locationId } : {}),
      });
      await qc.invalidateQueries();
      const itemLabel = itemGroups.flatMap((g) => g.items).find((o) => o.value === itemSel)?.label ?? "Ürün";
      onDone(`${itemLabel}: ${fmtQty(n)} tüketim kaydedildi (${res.created} hareket).`, res.warnings);
      onClose();
    } catch { /* banner */ }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Manuel Tüketim Kaydı"
      subtitle="Reçete / siparişten bağımsız tüketim"
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose} disabled={createMut.isPending}>İptal</button>
        <button type="submit" form="cons-form" className="btn-md primary" disabled={createMut.isPending}>
          {createMut.isPending ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </>}
    >
      <form id="cons-form" onSubmit={handleSubmit} noValidate>
        {err || createMut.error ? <div className="form-banner-err" role="alert">{err ?? createMut.error?.message}</div> : null}

        {/* Tarih + Vardiya */}
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cons-date">Tarih <span className="req">*</span></label>
            <input id="cons-date" className="form-input" type="date" value={date} max={istToday()}
              onChange={(e) => { setDate(e.target.value); setErr(null); }} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cons-shift">Vardiya</label>
            <select id="cons-shift" className="form-select" value={shiftId}
              onChange={(e) => { setShiftTouched(true); setShiftId(e.target.value); }}>
              <option value="">Otomatik (tarih/saatten)</option>
              {shifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.startTime}–{s.endTime})</option>)}
            </select>
          </div>
        </div>

        {/* Ürün */}
        <div className="form-field">
          <label className="lbl" htmlFor="cons-item">Ürün <span className="req">*</span></label>
          <SearchSelect
            id="cons-item"
            value={itemSel || null}
            options={itemGroups}
            onChange={(v) => { setItemSel(v ?? ""); setRowKey(""); setErr(null); }}
            placeholder="— Stok / Yarı Mamul / Mamul seçin —"
            searchPlaceholder="Ürün kodu / adı ara…"
            emptyText={itemOptsQ.isLoading ? "Yükleniyor…" : "Aktif ürün yok"}
          />
        </div>

        {/* Lot modu + (elle) lot seçimi / FEFO açıklaması */}
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cons-lotmode">Lot Seçimi</label>
            <select id="cons-lotmode" className="form-select" value={lotMode}
              onChange={(e) => { setLotMode(e.target.value as "fefo" | "manual"); setRowKey(""); }}>
              <option value="fefo">FEFO (otomatik · SKT'ye göre)</option>
              <option value="manual">Elle lot seç</option>
            </select>
          </div>
          {lotMode === "manual" ? (
            <div className="form-field">
              <label className="lbl" htmlFor="cons-lot">Lot + Lokasyon <span className="req">*</span></label>
              <SearchSelect
                id="cons-lot"
                value={rowKey || null}
                options={lotOptions}
                onChange={(v) => setRowKey(v ?? "")}
                placeholder={!itemId ? "Önce ürün seçin" : balQ.isLoading ? "Yükleniyor…" : lotOptions.length ? "— Lot seçin —" : "Serbest (released) lot yok"}
                searchPlaceholder="Lot / lokasyon ara…"
                disabled={!itemId || balQ.isLoading}
              />
              {selectedLot ? <span className="help">Bakiye: <b className="mono">{fmtQty(selectedLot.qty)} {selectedLot.uom}</b></span> : <span className="help">Yalnız serbest (released) + SKT geçmemiş lotlar listelenir.</span>}
            </div>
          ) : (
            <div className="form-field">
              <label className="lbl">Kaynak Lot</label>
              <div className="cons-note">SKT'ye göre en yakın uygun (serbest) lottan otomatik seçilir.</div>
            </div>
          )}
        </div>

        {/* Miktar + Sebep */}
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cons-qty">Miktar <span className="req">*</span></label>
            <input id="cons-qty" className="form-input" type="text" inputMode="decimal" value={qtyStr}
              onChange={(e) => { setQtyStr(e.target.value.replace(/[^\d.,]/g, "")); setErr(null); }}
              placeholder="örn. 12,5" />
            <span className="help">Ürünün temel biriminde.</span>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cons-reason">Sebep <span className="req">*</span></label>
            <select id="cons-reason" className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
              {REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
        </div>

        {/* Not */}
        <div className="form-field">
          <label className="lbl" htmlFor="cons-notes">Not</label>
          <input id="cons-notes" className="form-input" type="text" value={notes}
            onChange={(e) => setNotes(e.target.value)} maxLength={500} placeholder="opsiyonel açıklama" />
        </div>

        {/* Eksiye düşme onayı */}
        <label className="cons-check">
          <input type="checkbox" checked={allowNegative} onChange={(e) => setAllowNegative(e.target.checked)} />
          Stok yetersizse eksiye düşür (negatif stok)
        </label>
        {allowNegative ? (
          <div className="form-banner-info">
            Yeterli serbest lot yoksa kalan miktar son lota <b>negatif</b> yazılır; yalnız sayım düzeltmesiyle kapatılır.
          </div>
        ) : null}
      </form>
    </Drawer>
  );
}
